export const keywordPositioning = {
  frequency: {
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
  },
  device: {
    desktop: 'Desktop',
    mobile: 'Mobile',
  },
  list: {
    title: 'Keyword positioning',
    subtitle: 'Track how your domain ranks for the keywords that matter.',
    newAction: 'New list',
    error: 'Could not load your tracking lists.',
    empty: 'Create your first tracking list to start monitoring keyword positions.',
    emptyTitle: 'No tracking lists yet',
    card: {
      keywordCount_one: '{{count}} keyword',
      keywordCount_other: '{{count}} keywords',
      lastUpdated: 'Updated {{value}}',
      updatedNever: 'Updated never',
    },
    deleteDialog: {
      success: 'List deleted.',
      error: 'Could not delete the list.',
    },
  },
  create: {
    title: 'New tracking list',
    subtitle: 'Pick the domain, markets, and how often we should refresh positions.',
    success: 'List created',
    submit: 'Create list',
    submitting: 'Creating…',
    submitError: 'Could not create the list. Check your inputs and try again.',
    cancel: 'Cancel',
    fields: {
      name: {
        label: 'Name',
        placeholder: 'e.g. Acme — Core keywords',
      },
      domain: {
        label: 'Domain',
        placeholder: 'example.com',
      },
      countries: {
        label: 'Countries',
        placeholder: 'Select countries',
        searchPlaceholder: 'Search country',
        noResults: 'No countries match your search.',
        selectedCount_one: '{{count}} country selected',
        selectedCount_other: '{{count}} countries selected',
      },
      language: {
        label: 'Language',
        placeholder: 'es',
      },
      device: {
        label: 'Device',
      },
      frequency: {
        label: 'Refresh frequency',
      },
    },
    errors: {
      nameRequired: 'Name is required.',
      domainRequired: 'Domain is required.',
      countriesMin: 'Select at least one country',
      countriesMax: 'You can select up to 5 countries.',
      languageRequired: 'Language is required.',
    },
  },
  appearance: {
    organic: 'Organic',
    ai_overview: 'AI Overview',
    featured_snippet: 'Featured Snippet',
    not_found: 'Not found',
    aiOverviewCited: 'Cited in the AI Overview',
  },
  measurement: {
    unmeasurable: 'Not measured',
    unmeasurableHint_one:
      'We could not read the search engine for this keyword in the last update. This is not a ranking drop.',
    unmeasurableHint_other:
      'We could not read the search engine for this keyword in the last {{count}} updates. This is not a ranking drop.',
  },
  delta: {
    new: 'New',
    lost: 'Lost',
  },
  intent: {
    informational: 'Informational',
    navigational: 'Navigational',
    commercial: 'Commercial',
    transactional: 'Transactional',
  },
  intentStrength: {
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  },
  detail: {
    backToList: 'Back to lists',
    notFound: 'We could not find this tracking list.',
    lastUpdated: 'Updated {{value}}',
    updatedNever: 'Never updated',
    tabs: {
      dashboard: 'Dashboard',
      keywords: 'Keywords',
      competitors: 'Competitors',
    },
  },
  competitors: {
    tabLabel: 'Competitors',
    title: 'Competitors',
    subtitle: 'Add competitor domains to compare their positions against yours.',
    addPlaceholder: 'competitor.com',
    addCta: 'Add',
    limitReached: 'You can track up to 5 competitors per list.',
    duplicateError: 'This competitor is already being tracked.',
    deleteAction: 'Delete competitor',
    empty: 'No competitors yet. Add one above to start comparing positions.',
    error: 'Could not load competitors.',
    trackedFromNextUpdate: 'Competitors are tracked starting from the next update.',
    deleteDialog: {
      title: 'Delete competitor?',
      description: 'This will stop tracking "{{domain}}". You can add it again later.',
      cancel: 'Cancel',
      confirm: 'Delete',
    },
    toast: {
      added: 'Competitor added',
      addError: 'Could not add the competitor.',
      deleted: 'Competitor deleted',
      deleteError: 'Could not delete the competitor.',
    },
    importFromProfile: {
      action: 'Import from profile',
      title: 'Import competitors from profile',
      subtitle: 'Add competitors from your organization profile to this list.',
      remainingHint_one: 'You can add {{count}} more competitor.',
      remainingHint_other: 'You can add {{count}} more competitors.',
      alreadyTracked: 'Already tracked',
      cancel: 'Cancel',
      submitWithCount: 'Add selected ({{count}})',
      empty: 'No competitors in your profile yet.',
      error: 'Could not load competitor suggestions.',
      full: 'This list already tracks the maximum of 5 competitors.',
      toast: {
        imported: '{{added}} added, {{skipped}} skipped',
        importError: 'Could not import competitors.',
      },
    },
  },
  refresh: {
    updateNow: 'Update now',
    updating: 'Updating…',
    toast: {
      started:
        'Updating positions. This may take a few minutes depending on the number of keywords and countries.',
      heavy:
        'Updating positions. This may take several minutes given the number of keywords and countries.',
      updated: 'Positions updated',
      alreadyRunning: 'An update is already running',
      noKeywords: 'No keywords to update — add keywords to the list.',
      timedOut: 'Positions are still updating in the background — check back in a few minutes.',
      error: 'Could not start the update. Please try again.',
    },
  },
  keywords: {
    add: {
      title: 'Add keywords',
      hint: 'Press Enter to queue each keyword — or paste several lines. Everything is added in one batch.',
      placeholder: 'online store\npayment link\nmobile pos',
      submit: 'Add',
      submitWithCount: 'Add ({{count}})',
      removeQueued: 'Remove {{keyword}}',
    },
    importAction: 'Import from research',
    searchPlaceholder: 'Search keywords',
    columns: {
      keyword: 'Keyword',
      volume: 'Volume',
      intent: 'Intent',
      actions: 'Actions',
    },
    deleteAction: 'Delete keyword',
    error: 'Could not load keywords.',
    noMatches: 'No keywords match your search.',
    empty: {
      title: 'No keywords yet',
      subtitle: 'Add keywords above to start tracking their positions.',
      addCta: 'Add keywords',
      importCta: 'Import from research',
    },
    deleteDialog: {
      title: 'Delete keyword?',
      description: 'This will stop tracking "{{keyword}}". You can add it again later.',
      cancel: 'Cancel',
      confirm: 'Delete',
    },
    toast: {
      added_one: '{{count}} keyword added',
      added_other: '{{count}} keywords added',
      addedWithSkipped: '{{added}} added, {{skipped}} already existed',
      addError: 'Could not add keywords.',
      deleted: 'Keyword deleted',
      deleteError: 'Could not delete the keyword.',
    },
    importModal: {
      title: 'Import from research',
      subtitle: 'Pick a keyword research and choose which keywords to add to this list.',
      cancel: 'Cancel',
      submit: 'Import',
      selectedCount_one: '{{count}} keyword selected',
      selectedCount_other: '{{count}} keywords selected',
      research: {
        label: 'Keyword research',
        placeholder: 'Select a research',
        empty: 'No researches yet.',
        error: 'Could not load your researches.',
      },
      keywords: {
        searchPlaceholder: 'Search keywords',
        selectAll: 'Select all',
        empty: 'This research has no keywords.',
        noMatches: 'No keywords match your search.',
        error: 'Could not load keywords.',
        pageOf: 'Page {{page}} of {{totalPages}}',
        prevPage: 'Previous page',
        nextPage: 'Next page',
      },
      toast: {
        imported_one: '{{count}} keyword imported',
        imported_other: '{{count}} keywords imported',
        importedWithSkipped: '{{added}} imported, {{skipped}} already existed',
        importError: 'Could not import keywords.',
      },
    },
  },
  compare: {
    label: 'Compare to',
    customLabel: 'Comparison date',
    modes: {
      lastWeek: 'Last week',
      lastMonth: 'Last month',
      custom: 'Custom date',
    },
  },
  view: {
    label: 'View',
    positions: 'Positions',
    competencia: 'Competition',
  },
  competitive: {
    hero: {
      stat: 'keywords where you <0>lead</0> your {{competitors}} competitors',
      podium: 'on the podium (top 3)',
      avances: 'gains this week',
      alertas: 'alerts this week',
    },
    movements: {
      title: 'Movements · this week',
      passed: 'You passed <0>{{domain}}</0> in <1>“{{keyword}}”</1>',
      passedBy: '<0>{{domain}}</0> passed you in <1>“{{keyword}}”</1>',
      empty: 'Movements appear once there is a previous snapshot to compare against.',
    },
    columns: {
      keyword: 'Keyword',
      position: 'Your position',
      standing: 'Vs. competition',
      movement: 'Movement',
    },
    volume: '{{volume}} vol',
    rank: {
      chip: '{{rank}}º of {{total}}',
      unranked: 'Unranked',
    },
    beatenBy: 'you lose to {{names}}',
    youBeatAll: 'you beat all {{count}}',
    pip: {
      ahead: '{{domain}} #{{position}}',
      behind: '{{domain}} #{{position}}',
      none: '{{domain}} not ranking',
    },
    rowMovement: {
      passed: 'passed {{domain}}',
      passedBy: '{{domain}} passed you',
      none: 'no change',
    },
    hint: '<0>Competition view</0> hides type, intent and URL to focus on how you compare. Switch to “Positions” for the full detail.',
    empty: {
      noKeywords: 'No keywords to compare yet. Add keywords and run an update.',
      noSnapshot: 'No data yet — run an update to compare against your competitors.',
    },
    error: 'Could not load the competitive standing.',
  },
  summary: {
    error: 'Could not load the summary.',
    empty: {
      title: 'No data yet — run an update',
      subtitle: 'Use the "Update now" button to fetch positions for this list.',
      noKeywordsTitle: 'Add keywords to start tracking',
      noKeywordsSubtitle:
        'This list has no keywords yet. Add the keywords you want to track and then run an update to see positions.',
      addKeywordsCta: 'Add keywords',
      runUpdateCta: 'Run update',
    },
    totalKeywords: 'Total keywords',
    topBuckets: 'Top positions',
    top3: 'Top 3',
    top10: 'Top 10',
    top20: 'Top 20',
    changes: 'Changes',
    improved: 'Improved',
    declined: 'Declined',
    new: 'New',
    lost: 'Lost',
    appearance: 'Appearance',
    meta: 'Last updated {{updated}} · Frequency: {{frequency}}',
    comparedAgainst: 'Changes compared against {{date}}',
    noComparison: 'No previous snapshot yet — changes will show from the next update',
    aiOverviewOverlayHint:
      'Counted IN ADDITION to the organic appearance: a keyword can rank and be cited in the AI Overview at once, so this panel can add up to more than the keyword total.',
  },
  table: {
    error: 'Could not load positions.',
    showing: 'Showing {{shown}} of {{total}}',
    sortByPosition: 'Sort by position',
    showCompetitors: 'Competitors',
    columns: {
      keyword: 'Keyword',
      searchVolume: 'Search Vol',
      position: 'Position',
      type: 'Type',
      intent: 'Intent',
      delta: 'Change',
      url: 'URL',
    },
    competitorColumnTooltip: 'Competitor position for {{domain}}',
    filters: {
      all: 'All',
      searchLabel: 'Search',
      searchPlaceholder: 'Search keywords',
      appearanceLabel: 'Appearance',
      bucketLabel: 'Position',
    },
    buckets: {
      TOP_3: 'Top 3',
      TOP_10: 'Top 10',
      TOP_20: 'Top 20',
      TOP_100: 'Top 100',
    },
    empty: {
      noDataForDate: 'No data for this date. Closest snapshot: {{date}}',
      viewNearest: 'View closest snapshot',
      noMatches: 'No keywords match your filters.',
    },
  },
} as const
